import { Box } from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";
import { visuallyHidden } from "@mui/utils";
import { useTranslation } from "react-i18next";

export type BarTier =
  | "presidential"
  | "national"
  | "participation"
  | "incomplete";

export interface StackedBarSegment {
  tier: BarTier;
  count: number;
}

export interface StackedBarGroup {
  label: string;
  segments: StackedBarSegment[];
}

export interface StackedBarProps {
  /** Rendered in the given order; never sorted. */
  groups: StackedBarGroup[];
  showLegend?: boolean;
}

const TIERS: BarTier[] = [
  "presidential",
  "national",
  "participation",
  "incomplete",
];

const colorFor = (theme: Theme, tier: BarTier) => {
  const p = theme.palette;
  switch (tier) {
    case "presidential":
      return p.tierPresidential;
    case "national":
      return p.tierNational;
    case "participation":
      return p.gold;
    default:
      return p.text.disabled;
  }
};

/** Horizontal stacked bar per group, segments by award tier, with text totals. */
export const StackedBar = ({ groups, showLegend = true }: StackedBarProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const tierLabel = (tier: BarTier) =>
    t(`awards.${tier}`, { defaultValue: tier });

  return (
    <Box>
      <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none" }}>
        {groups.map((group, index) => {
          const total = group.segments.reduce((s, x) => s + x.count, 0);
          const summary = group.segments
            .map((s) => `${s.count} ${tierLabel(s.tier)}`)
            .join(", ");
          return (
            <Box
              component="li"
              key={`${group.label}-${index}`}
              sx={{ display: "flex", alignItems: "center", gap: 2, py: 0.75 }}
            >
              <Box
                sx={{
                  width: 150,
                  flexShrink: 0,
                  fontSize: 13,
                  fontWeight: 700,
                }}
              >
                {group.label}
              </Box>
              <Box
                role="img"
                aria-label={`${group.label}: ${summary}`}
                data-testid="stacked-bar"
                sx={{
                  flex: 1,
                  display: "flex",
                  height: 22,
                  overflow: "hidden",
                  borderRadius: "6px",
                  bgcolor: "track",
                }}
              >
                {group.segments.map((seg) =>
                  seg.count > 0 ? (
                    <Box
                      key={seg.tier}
                      data-testid="bar-segment"
                      data-tier={seg.tier}
                      sx={{
                        width: total > 0 ? `${(seg.count / total) * 100}%` : 0,
                        bgcolor: colorFor(theme, seg.tier),
                        // 2 px gap between segments, drawn in the card color.
                        boxShadow: `inset -2px 0 0 ${theme.palette.background.paper}`,
                        "&:last-of-type": { boxShadow: "none" },
                      }}
                    >
                      <span style={visuallyHidden}>
                        {`${seg.count} ${tierLabel(seg.tier)}`}
                      </span>
                    </Box>
                  ) : null,
                )}
              </Box>
              <Box
                sx={{
                  width: 54,
                  textAlign: "right",
                  fontSize: 13,
                  fontWeight: 700,
                  fontStretch: "62%",
                  color: "text.secondary",
                }}
              >
                <span style={visuallyHidden}>
                  {t("reports.total", { defaultValue: "Total" })}{" "}
                </span>
                {total}
              </Box>
            </Box>
          );
        })}
      </Box>
      {showLegend && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            mt: 1.5,
            fontSize: 11.5,
            fontWeight: 600,
            color: "text.secondary",
          }}
          data-testid="stacked-legend"
        >
          {TIERS.map((tier) => (
            <Box
              key={tier}
              sx={{ display: "flex", alignItems: "center", gap: 0.625 }}
            >
              <Box
                aria-hidden
                sx={{
                  width: 9,
                  height: 9,
                  borderRadius: "2px",
                  bgcolor: colorFor(theme, tier),
                }}
              />
              {tierLabel(tier)}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default StackedBar;
